import {useEffect} from 'react'
import getLangBySubdomain from './getLangBySubdomain'

//share images only exist for these languages; other subdomains fall back to English
const OG_IMAGE_LANGS = ['ua', 'pl', 'en']
const OG_LOCALES = {ua: 'uk_UA', pl: 'pl_PL', en: 'en_US', sk: 'sk_SK', cs: 'cs_CZ', de: 'de_DE'}
const ogImageLang = () => OG_IMAGE_LANGS.includes(getLangBySubdomain()) ? getLangBySubdomain() : 'en'

const metaProps = {
    serviceTitle: 'LoveInBook',
    description: '',
    image: `assets/images/${ogImageLang()}/LoveInBook.png`,
    origin: '',
    domain: ''
}

setOrigin(window.location.origin)

export function initMeta(appMetaProps) {
    const {origin, ...props} = appMetaProps
    Object.assign(metaProps, props)
    if (origin) {
        setOrigin(origin)
    }
}

function setOrigin(origin) {
    metaProps.origin = origin
    metaProps.domain = origin.replace(/https?:\/\//, '')
}

function formatPageTitle(title) {
    if (!title)
        return metaProps.serviceTitle
    return title
}

function generateCanonicalLink(params, canonicalUrl) {
    return {
        tag: 'link',
        locator: 'rel',
        tags: [
            {name: 'canonical', content: canonicalUrl, attribute: 'href'}
        ]
    }
}

function generateDescriptionMeta({description}) {
    return {
        locator: 'name',
        tags: [
            {name: 'description', content: description || metaProps.description}
        ]
    }
}

function generateOpenGraphMeta({description, title, image}, canonicalUrl) {
    //imageEndpoint
    return {
        locator: 'property',
        tags: [
            {name: 'og:title', content: formatPageTitle(title)},
            {name: 'og:url', content: canonicalUrl},
            {name: 'og:site_name', content: formatPageTitle(metaProps.serviceTitle)},
            {name: 'og:description', content: description || metaProps.description},
            {name: 'og:type', content: 'website'},
            {name: 'og:locale', content: OG_LOCALES[getLangBySubdomain()] || 'uk_UA'},
            {name: 'og:image:width', content: 1200},
            {name: 'og:image:height', content: 630},
            {name: 'og:image', content: formatPageImage()}
        ]
    }
}

function generateTwitterMeta({description, title}) {
    return {
        locator: 'name',
        tags: [
            {name: 'twitter:card', content: 'summary_large_image'},
            {name: 'twitter:title', content: formatPageTitle(title)},
            {name: 'twitter:description', content: description || metaProps.description},
            {name: 'twitter:image', content: formatPageImage()}
        ]
    }
}

function generateLdJsonSchema({json, title, description, image}, canonicalUrl) {
    return {
        tag: 'script',
        locator: 'type',
        tags: [
            {
                name: 'application/ld+json',
                content: JSON.stringify(json ? json :{
                    '@context': 'http://schema.org',
                    '@type': 'WebPage',
                    name: formatPageTitle(title),
                    description: description || metaProps.description,
                    url: canonicalUrl,
                    thumbnailUrl: formatPageImage(),
                    publisher: {
                        '@type': 'ProfilePage',
                        name: metaProps.serviceTitle
                    }
                })
            }
        ]
    }
}

function replaceMetaTags(replacement) {
    const {tag, tags, locator} = replacement
    const selector = tag || 'meta'
    tags.forEach(tagSettings => {
        let tag = document.querySelector(`${selector}[${locator}="${tagSettings.name}"]`)
        if (!tag) {
            tag = createTag(selector)
            tag.setAttribute(locator, tagSettings.name)
        }
        if (selector === 'meta') {
            tag.content = tagSettings.content
        } else if (tagSettings.attribute) {
            tag[tagSettings.attribute] = tagSettings.content
        } else {
            tag.innerText = tagSettings.content
        }
    })
}

function createTag(tagName, props) {
    let tag = document.createElement(tagName)
    for (let key in props) {
        tag[key] = props[key]
    }
    document.head.appendChild(tag)
    return tag
}

function removeTag(selector) {
    let tag = document.querySelector(selector)
    if (tag) {
        tag.parentElement.removeChild(tag)
    }
}

//During prerender the app is served as <sub>.localhost — map that back to the real production host
//so the baked-in canonical / og:url / og:image point at loveinbook.com, not localhost.
function productionOrigin() {
    const {hostname, origin} = window.location
    if (hostname.endsWith('.localhost') || hostname === 'localhost') {
        const sub = hostname.split('.')[0]
        return (sub && sub !== 'localhost') ? `https://${sub}.loveinbook.com` : 'https://loveinbook.com'
    }
    return origin
}

//full per-page canonical (origin + path). Previously this returned only the origin, so every page
//declared the homepage as its canonical.
function formatCanonicalUrl() {
    return productionOrigin() + window.location.pathname
}

function formatPageImage() {
    return productionOrigin() + '/' + metaProps.image
}

let pageMeta = {}

const tagReplacerPipeline = [
    generateCanonicalLink,
    generateDescriptionMeta,
    generateOpenGraphMeta,
    generateTwitterMeta,
    generateLdJsonSchema
] // generateItemPropSchema

function setPageMetadata(meta = {}) {
    JSON.stringify(pageMeta)
    if (JSON.stringify(pageMeta) === JSON.stringify(meta))
        return
    //generate canonical URL
    const canonicalUrl = formatCanonicalUrl()
    document.title = formatPageTitle(meta.title)
    for (const replacer of tagReplacerPipeline) {
        replaceMetaTags(replacer(meta, canonicalUrl))
    }
    if (meta.customMeta) {
        replaceMetaTags(meta.customMeta)
    }
    pageMeta = meta
}

function resetPageMetadata(meta) {
    setPageMetadata({
        title: metaProps.serviceTitle,
        description: metaProps.description,
        image: metaProps.image
    })
    //TODO: add logic to cleanup custom page meta tags on page unload
}

export function usePageMetadata(meta) {
    useEffect(() => {
        setPageMetadata(meta)
        return () => resetPageMetadata(meta)
    }, [JSON.stringify(meta), [formatCanonicalUrl()]])
}

export function setPageNoIndex(noIndex) {
    if (!noIndex) {
        removeTag('meta[name=robots]')
    } else {
        replaceMetaTags({
            locator: 'name',
            tags: [
                {name: 'robots', content: 'noindex,nofollow'}
            ]
        })
    }
}